/**
 * Cross-browser fullscreen + orientation-lock helpers. All entry points must
 * be called from a user gesture. Everything is best-effort: iPhone Safari does
 * not support the Fullscreen API on non-video elements and orientation lock is
 * widely restricted, so each call degrades silently rather than throwing.
 */

interface FullscreenEl extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}
interface LockableOrientation extends ScreenOrientation {
  lock?: (orientation: 'landscape') => Promise<void>;
}

export function isFullscreenSupported(): boolean {
  const el = document.documentElement as FullscreenEl;
  return (
    typeof el.requestFullscreen === 'function' || typeof el.webkitRequestFullscreen === 'function'
  );
}

export function isFullscreen(): boolean {
  return document.fullscreenElement != null;
}

export async function enterFullscreen(
  target: HTMLElement = document.documentElement,
): Promise<void> {
  const el = target as FullscreenEl;
  try {
    if (typeof el.requestFullscreen === 'function') {
      await el.requestFullscreen();
    } else if (typeof el.webkitRequestFullscreen === 'function') {
      await el.webkitRequestFullscreen();
    }
  } catch {
    /* denied or unsupported — non-fatal */
  }
}

/** Best-effort landscape lock (only works in fullscreen on most browsers). */
export async function lockLandscape(): Promise<void> {
  try {
    const orientation = screen.orientation as LockableOrientation | undefined;
    await orientation?.lock?.('landscape');
  } catch {
    /* not supported / not permitted — non-fatal */
  }
}
